/**
 * HEDEF: ONEDRIVE — uygulama klasörü (`Uygulamalar/<uygulama adı>`).
 *
 * Kapsam `Files.ReadWrite.AppFolder`: Parafon yalnızca kendi klasörünü görür.
 *
 * Yetki: OAuth 2 + PKCE, "tek sayfa uygulaması" (SPA) olarak kayıtlı dönüş
 * adresiyle. SINIR — GÜNDE TEK TIK: Microsoft SPA'lara verdiği yenileme
 * anahtarının ömrünü İLK GİRİŞTEN itibaren 24 saatle sınırlıyor ve
 * yenilemeler bu süreyi uzatmıyor. 24 saat içinde yedek sessizce sürer;
 * sonra "OneDrive'a yeniden bağlan" düğmesi görünür (Microsoft oturumu
 * açıksa tık pencereyi kendiliğinden kapatır).
 */

import { M } from "../../dil";
import type { BulutDosyasi, BulutHedefi, ErisimAnahtari, HedefKaydi } from "../tipler";
import { BaglantiGerekliHatasi } from "../yedekleyici";
import {
  BelirtecHatasi,
  bitisHesapla,
  donusAdresi,
  formGonder,
  gecerliMi,
  pkceUret,
  rastgele,
} from "../oauth";
import type { BulutBagimliliklari, Pkce } from "../oauth";

const KIRACI = "https://login.microsoftonline.com/common/oauth2/v2.0";
const GRAPH = "https://graph.microsoft.com/v1.0/me/drive/special/approot";
const KAPSAM = "Files.ReadWrite.AppFolder offline_access";
/** Microsoft'un SPA yenileme anahtarı için belgelediği sert sınır. */
const SPA_YENILEME_OMRU_MS = 24 * 3600_000;

type OneDriveKaydi = Extract<HedefKaydi, { tur: "onedrive" }>;

export async function onedriveBaglan(istemci: string, d: BulutBagimliliklari): Promise<OneDriveKaydi> {
  const state = rastgele();
  const tut: { pkce: Pkce | null } = { pkce: null };
  const params = await d.yetkilendir(async () => {
    const pkce = await pkceUret();
    tut.pkce = pkce;
    return `${KIRACI}/authorize?${new URLSearchParams({
      client_id: istemci,
      response_type: "code",
      redirect_uri: donusAdresi(),
      response_mode: "query",
      scope: KAPSAM,
      code_challenge: pkce.meydanOkuma,
      code_challenge_method: "S256",
      state,
    }).toString()}`;
  }, state);
  const kod = params.get("code");
  if (kod === null || tut.pkce === null) throw new Error(M.yedek.hedef.onedriveKodYok);
  const simdi = d.saat();
  const g = await formGonder(d.fetch, `${KIRACI}/token`, {
    client_id: istemci,
    grant_type: "authorization_code",
    code: kod,
    redirect_uri: donusAdresi(),
    code_verifier: tut.pkce.dogrulayici,
    scope: KAPSAM,
  });
  if (typeof g["access_token"] !== "string") throw new Error(M.yedek.hedef.onedriveAnahtarYok);
  return {
    tur: "onedrive",
    yenilemeAnahtari: typeof g["refresh_token"] === "string" ? g["refresh_token"] : null,
    yenilemeBitisi: new Date(simdi.getTime() + SPA_YENILEME_OMRU_MS).toISOString(),
    erisim: { deger: g["access_token"], bitis: bitisHesapla(simdi, g["expires_in"]) },
  };
}

/** Dosya adı yol parçası olarak: `:` ve `/` gibi karakterler kaçırılır. */
function yol(ad: string): string {
  return `${GRAPH}:/${encodeURIComponent(ad)}`;
}

export function onedriveHedefi(ilk: OneDriveKaydi, istemci: string, d: BulutBagimliliklari): BulutHedefi {
  let kayit: OneDriveKaydi = ilk;

  function yenilenebilir(): boolean {
    return kayit.yenilemeAnahtari !== null && gecerliMi(kayit.yenilemeBitisi, d.saat());
  }

  async function yenile(): Promise<string> {
    if (!yenilenebilir()) {
      throw new BaglantiGerekliHatasi(M.yedek.hedef.onedriveGunlukDoldu);
    }
    try {
      const g = await formGonder(d.fetch, `${KIRACI}/token`, {
        client_id: istemci,
        grant_type: "refresh_token",
        refresh_token: kayit.yenilemeAnahtari ?? "",
        scope: KAPSAM,
      });
      const erisim: ErisimAnahtari = {
        deger: String(g["access_token"]),
        bitis: bitisHesapla(d.saat(), g["expires_in"]),
      };
      kayit = {
        ...kayit,
        erisim,
        // Yenileme anahtarı her kullanımda kendini değiştiriyor; SÜRESİ değişmiyor.
        yenilemeAnahtari: typeof g["refresh_token"] === "string" ? g["refresh_token"] : kayit.yenilemeAnahtari,
      };
      return erisim.deger;
    } catch (e: unknown) {
      if (e instanceof BelirtecHatasi && (e.kod === "invalid_grant" || e.durum === 400 || e.durum === 401)) {
        kayit = { ...kayit, yenilemeAnahtari: null };
        throw new BaglantiGerekliHatasi(M.yedek.hedef.onedriveDustu);
      }
      throw e;
    }
  }

  async function anahtar(): Promise<string> {
    if (kayit.erisim !== null && gecerliMi(kayit.erisim.bitis, d.saat())) return kayit.erisim.deger;
    return yenile();
  }

  async function cagir(url: string, init: RequestInit = {}): Promise<Response> {
    for (let deneme = 0; deneme < 2; deneme += 1) {
      const a = deneme === 0 ? await anahtar() : await yenile();
      const y = await d.fetch(url, {
        ...init,
        headers: { ...(init.headers as Record<string, string> | undefined), Authorization: `Bearer ${a}` },
      });
      if (y.status === 401 && deneme === 0) continue;
      if (y.status === 401) throw new BaglantiGerekliHatasi(M.yedek.hedef.onedriveReddetti);
      if (!y.ok) {
        const metin = await y.text().catch(() => "");
        throw new Error(`OneDrive ${y.status}: ${metin.slice(0, 200)}`);
      }
      return y;
    }
    throw new BaglantiGerekliHatasi(M.yedek.hedef.onedriveReddetti);
  }

  return {
    tur: "onedrive",
    konum: M.yedek.hedef.onedriveKonum,
    hazirlik: () =>
      Promise.resolve(
        (kayit.erisim !== null && gecerliMi(kayit.erisim.bitis, d.saat())) || yenilenebilir()
          ? "hazir"
          : "etkilesim-gerekli",
      ),
    async yenidenBaglan() {
      kayit = await onedriveBaglan(istemci, d);
    },
    async yaz(ad, metin) {
      await cagir(`${yol(ad)}:/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/octet-stream" },
        body: metin,
      });
    },
    async listele() {
      const out: BulutDosyasi[] = [];
      let url: string | null = `${GRAPH}/children?$select=name,lastModifiedDateTime,size,file&$top=200`;
      while (url !== null) {
        const g = (await (await cagir(url)).json()) as {
          value?: { name: string; lastModifiedDateTime?: string; size?: number; file?: unknown }[];
          "@odata.nextLink"?: string;
        };
        for (const f of g.value ?? []) {
          if (f.file !== undefined && f.name.endsWith(".pfai")) {
            out.push({ ad: f.name, degisti: f.lastModifiedDateTime ?? null, boyut: f.size ?? null });
          }
        }
        url = g["@odata.nextLink"] ?? null;
      }
      return out;
    },
    async oku(ad) {
      // İçerik uç noktası 302 ile ÖN-YETKİLİ bir indirme adresine yönlendiriyor;
      // yönlendirmeyi fetch'e bırakmak yerine adresi üst veriden alıyoruz
      // (başka kökene yönlendirmede Authorization başlığı düşürülüyor ve
      // davranış tarayıcıdan tarayıcıya değişiyor).
      const ust = (await (await cagir(yol(ad))).json()) as Record<string, unknown>;
      const indir = ust["@microsoft.graph.downloadUrl"];
      if (typeof indir !== "string") throw new Error(M.yedek.hedef.onedriveIndirmeAdresiYok(ad));
      const y = await d.fetch(indir);
      if (!y.ok) throw new Error(M.yedek.hedef.onedriveIndirme(y.status));
      return y.text();
    },
    async sil(ad) {
      await cagir(yol(ad), { method: "DELETE" });
    },
    kayit: () => kayit,
  };
}
